import React, { useState, Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, BookOpenIcon, StethoscopeIcon, GraduationCapIcon, TargetIcon, TrendingUpIcon, SmartphoneIcon, FileTextIcon, LayersIcon, HelpCircleIcon, ClipboardListIcon, CalendarCheckIcon, ChevronLeftIcon, ChevronRightIcon, QuoteIcon, CheckCircle2Icon, TrophyIcon } from 'lucide-react';
const BLUE = '#2563EB';
const TEAL = '#0F9E8E';
const REDI_IMG = 'https://cdn.magicpatterns.com/uploads/cuSDustQLFMoA2E1aRSeBS/file_00000000117c71f5a64341e87ebc6818.png';
const rdReadyPoints = ['Study Guide', 'Flashcards', 'Practice Questions', 'Mock Exams', 'Progress Tracking'];
const cnscPoints = ['Content Domains', 'Practice Exams', 'Clinical Calculations', 'Case Studies', 'Flashcards'];
const whyPoints = [{
  icon: GraduationCapIcon,
  title: 'Evidence-Based Content',
  text: 'Created by Registered Dietitians and reviewed for accuracy.'
}, {
  icon: TargetIcon,
  title: 'Exam-Focused Learning',
  text: 'High-yield material that matches the exam blueprints.'
}, {
  icon: TrendingUpIcon,
  title: 'Track Your Progress',
  text: 'Monitor your progress and focus on what matters most.'
}, {
  icon: SmartphoneIcon,
  title: 'Study Anywhere',
  text: 'Access your materials anytime, anywhere, on any device.'
}];
const included = [{
  icon: FileTextIcon,
  label: 'Study Guides'
}, {
  icon: LayersIcon,
  label: 'Flashcards'
}, {
  icon: HelpCircleIcon,
  label: 'Practice Questions'
}, {
  icon: ClipboardListIcon,
  label: 'Mock Exams'
}, {
  icon: TrendingUpIcon,
  label: 'Progress Tracking'
}, {
  icon: CalendarCheckIcon,
  label: 'Study Plans'
}];
const testimonials = [{
  quote: 'Rd Ready gave me the structure and confidence I needed to finally pass the RD exam. The practice questions and explanations were exactly what I needed!',
  name: 'Jenna M., RD'
}, {
  quote: 'The CNSC content domains and clinical calculations were broken down so clearly. I walked into my exam feeling genuinely prepared.',
  name: 'Marcus T., RDN'
}, {
  quote: 'Being able to study anywhere and track my progress kept me motivated the whole way through. Worth every minute.',
  name: 'Priya S., RD'
}];
const team: {
  name: string;
  role: string;
  color: string;
  img?: string;
}[] = [{
  name: 'Ardy',
  role: 'Evidence-based explanations.',
  color: '#2563EB'
}, {
  name: 'Redi',
  role: 'Keeps you motivated.',
  color: '#0F9E8E',
  img: REDI_IMG
}, {
  name: 'Rea',
  role: 'Breaks down difficult concepts.',
  color: '#7C3AED'
}, {
  name: 'Ari',
  role: 'Creates study plans and strategies.',
  color: '#EA580C'
}, {
  name: 'Dee',
  role: 'Simplifies complex topics.',
  color: '#DB2777'
}];
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 22
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
function MascotAvatar({
  color,
  size = 'h-16 w-16'
}: {
  color: string;
  size?: string;
}) {
  return <span className={`${size} flex items-center justify-center rounded-full text-white`} style={{
    backgroundColor: color
  }} aria-hidden="true" data-id="element-2514">
      <GraduationCapIcon className="h-1/2 w-1/2" strokeWidth={2} data-id="element-2515" />
    </span>;
}
export function RDReadyHome() {
  const [active, setActive] = useState(0);
  const total = testimonials.length;
  const go = (dir: number) => setActive(i => (i + dir + total) % total);
  return <div className="w-full overflow-hidden bg-white text-[#1E293B]" style={{
    fontFamily: '"Poppins", "Inter", system-ui, sans-serif'
  }} data-id="element-2516">
      {/* HERO */}
      <section className="relative px-5 pt-14 pb-10 md:px-8 md:pt-20 md:pb-16" data-id="element-2517">
        <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[1.05fr_.95fr]" data-id="element-2518">
          <motion.div initial="hidden" animate="visible" variants={{
          visible: {
            transition: {
              staggerChildren: 0.1
            }
          }
        }} data-id="element-2519">
            <motion.h1 variants={fadeUp} transition={{
            duration: 0.5
          }} className="text-4xl font-extrabold leading-[1.08] tracking-tight text-[#0F172A] sm:text-5xl lg:text-6xl" data-id="element-2520">
              Pass the{' '}
              <span style={{
              color: BLUE
            }} data-id="element-2521">
                RD Exam.
              </span>
              <br data-id="element-2522" />
              Master the{' '}
              <span style={{
              color: TEAL
            }} data-id="element-2523">
                CNSC.
              </span>
            </motion.h1>
            <motion.p variants={fadeUp} transition={{
            duration: 0.5
          }} className="mt-6 max-w-md text-base leading-relaxed text-[#5A6B7B]" data-id="element-2524">
              Evidence-based study tools built by dietitians to help you pass
              your certification exam with confidence.
            </motion.p>
            <motion.div variants={fadeUp} transition={{
            duration: 0.5
          }} className="mt-8 flex flex-col gap-3 sm:flex-row" data-id="element-2525">
              <Link to="/rd-exam" className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-white shadow-sm transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2" style={{
              backgroundColor: BLUE
            }} data-id="element-2526">
                Explore RD Exam <ArrowRightIcon className="h-4 w-4" data-id="element-2527" />
              </Link>
              <Link to="/cnsc-exam" className="inline-flex items-center justify-center gap-2 rounded-full border-2 px-7 py-3.5 text-sm font-semibold transition-colors hover:bg-[#0F9E8E]/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2" style={{
              borderColor: TEAL,
              color: TEAL
            }} data-id="element-2528">
                Explore CNSC Exam <ArrowRightIcon className="h-4 w-4" data-id="element-2529" />
              </Link>
            </motion.div>
          </motion.div>

          {/* Hero illustration area */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.15
        }} className="relative flex items-center justify-center" data-id="element-2530">
            <div className="relative flex aspect-square w-full max-w-md items-center justify-center" data-id="element-2531">
              <div className="absolute inset-6 rounded-full bg-[#EEF4FF]" data-id="element-2532" />
              <img src={REDI_IMG} alt="Redi, your RD Ready study guide, waving and holding a Nutrition Science book" className="relative z-10 w-[86%] object-contain drop-shadow-sm" data-id="element-2533" />
              <div className="absolute left-0 top-6 z-20 max-w-[190px] rounded-2xl rounded-bl-sm bg-white p-4 shadow-lg" data-id="element-2534">
                <p className="text-sm font-semibold text-[#0F172A]" data-id="element-2535">
                  Hi! I&apos;m{' '}
                  <span style={{
                  color: BLUE
                }} data-id="element-2536">
                    Redi
                  </span>
                </p>
                <p className="mt-1 text-xs leading-relaxed text-[#5A6B7B]" data-id="element-2537">
                  Which exam are you studying for?
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TWO PROGRAM CARDS */}
      <section className="px-5 py-6 md:px-8 md:py-10" data-id="element-2538">
        <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-2" data-id="element-2539">
          <ProgramCard accent={BLUE} bg="#F5F8FF" icon={BookOpenIcon} title="RD Ready" blurb="Everything you need to prepare for the Registered Dietitian exam." points={rdReadyPoints} cta="Explore RD Ready" to="/rd-exam" data-id="element-2540" />
          <ProgramCard accent={TEAL} bg="#F0FBF9" icon={StethoscopeIcon} title="CNSC Ready" blurb="High-yield preparation for the Certified Nutrition Support Clinician exam." points={cnscPoints} cta="Explore CNSC Ready" to="/cnsc-exam" data-id="element-2541" />
        </div>
      </section>

      {/* WHY STUDENTS CHOOSE */}
      <section className="px-5 py-12 md:px-8 md:py-16" data-id="element-2542">
        <div className="mx-auto max-w-7xl rounded-3xl bg-[#F7F9FC] px-6 py-12 md:px-10 md:py-14" data-id="element-2543">
          <h2 className="text-center text-2xl font-bold text-[#0F172A] md:text-3xl" data-id="element-2544">
            Why Students Choose Rd Ready
          </h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-2545">
            {whyPoints.map(({
            icon: Icon,
            title,
            text
          }, i) => <motion.article key={title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.08
          }} className="rounded-2xl bg-white p-6 text-center shadow-[0_2px_16px_rgba(15,23,42,.05)]" data-id="element-2546">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full" style={{
              backgroundColor: '#EEF4FF'
            }} data-id="element-2547">
                  <Icon className="h-6 w-6" style={{
                color: BLUE
              }} data-id="element-2548" />
                </span>
                <h3 className="mt-4 text-base font-bold text-[#0F172A]" data-id="element-2549">
                  {title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-[#5A6B7B]" data-id="element-2550">
                  {text}
                </p>
              </motion.article>)}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="px-5 py-8 md:px-8 md:py-12" data-id="element-2551">
        <div className="mx-auto max-w-5xl" data-id="element-2552">
          <h2 className="text-center text-2xl font-bold text-[#0F172A] md:text-3xl" data-id="element-2553">
            What Our Students Say
          </h2>
          <div className="mt-8 flex items-center gap-3 md:gap-5" data-id="element-2554">
            <button type="button" onClick={() => go(-1)} aria-label="Previous testimonial" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#E2E8F0] text-[#5A6B7B] transition-colors hover:border-[#2563EB] hover:text-[#2563EB]" data-id="element-2555">
              <ChevronLeftIcon className="h-5 w-5" data-id="element-2556" />
            </button>
            <motion.blockquote key={active} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.35
          }} className="flex-1 rounded-2xl bg-[#F7F9FC] p-7 md:p-9" data-id="element-2557">
              <QuoteIcon className="h-7 w-7" style={{
              color: BLUE
            }} fill="currentColor" data-id="element-2558" />
              <p className="mt-4 text-base leading-relaxed text-[#334155]" data-id="element-2559">
                {testimonials[active].quote}
              </p>
              <footer className="mt-4 text-sm font-semibold" style={{
              color: BLUE
            }} data-id="element-2560">
                — {testimonials[active].name}
              </footer>
            </motion.blockquote>
            <button type="button" onClick={() => go(1)} aria-label="Next testimonial" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#E2E8F0] text-[#5A6B7B] transition-colors hover:border-[#2563EB] hover:text-[#2563EB]" data-id="element-2561">
              <ChevronRightIcon className="h-5 w-5" data-id="element-2562" />
            </button>
          </div>
          <div className="mt-6 flex justify-center gap-2" data-id="element-2563">
            {testimonials.map((_, i) => <button key={i} type="button" onClick={() => setActive(i)} aria-label={`Go to testimonial ${i + 1}`} className="h-2.5 w-2.5 rounded-full transition-colors" style={{
            backgroundColor: i === active ? BLUE : '#CBD5E1'
          }} data-id="element-2564" />)}
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="px-5 py-12 md:px-8 md:py-16" data-id="element-2565">
        <div className="mx-auto max-w-5xl" data-id="element-2566">
          <h2 className="text-center text-2xl font-bold text-[#0F172A] md:text-3xl" data-id="element-2567">
            What&apos;s Included
          </h2>
          <div className="mt-10 grid grid-cols-2 gap-8 sm:grid-cols-3 md:grid-cols-6" data-id="element-2568">
            {included.map(({
            icon: Icon,
            label
          }) => <div key={label} className="flex flex-col items-center text-center" data-id="element-2569">
                <Icon className="h-9 w-9" style={{
              color: BLUE
            }} strokeWidth={1.75} data-id="element-2570" />
                <p className="mt-3 text-xs font-semibold leading-tight text-[#334155]" data-id="element-2571">
                  {label}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* MEET YOUR STUDY TEAM */}
      <section className="px-5 py-8 md:px-8 md:py-12" data-id="element-2572">
        <div className="mx-auto max-w-5xl" data-id="element-2573">
          <h2 className="text-center text-2xl font-bold text-[#0F172A] md:text-3xl" data-id="element-2574">
            Meet Your Study Team
          </h2>
          <div className="mt-10 grid grid-cols-2 gap-8 sm:grid-cols-3 md:grid-cols-5" data-id="element-2575">
            {team.map(member => <div key={member.name} className="flex flex-col items-center text-center" data-id="element-2576">
                {member.img ? <span className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-[#EEF4FF]" data-id="element-2577">
                    <img src={member.img} alt={`${member.name}, RD Ready study team member`} className="h-full w-full scale-125 object-contain object-top" data-id="element-2578" />
                  </span> : <MascotAvatar color={member.color} data-id="element-2579" />}
                <p className="mt-3 text-2xl leading-none" style={{
              fontFamily: '"Dancing Script", cursive',
              color: member.color
            }} data-id="element-2580">
                  {member.name}
                </p>
                <p className="mt-1 text-xs leading-tight text-[#5A6B7B]" data-id="element-2581">
                  {member.role}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA BAR */}
      <section className="px-5 pb-14 pt-6 md:px-8 md:pb-20" data-id="element-2582">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 rounded-2xl bg-[#F7F9FC] px-6 py-6 md:flex-row md:px-10" data-id="element-2583">
          <div className="flex items-center gap-4" data-id="element-2584">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white" style={{
            backgroundColor: BLUE
          }} data-id="element-2585">
              <TrophyIcon className="h-5 w-5" data-id="element-2586" />
            </span>
            <div data-id="element-2587">
              <p className="text-base font-bold text-[#0F172A]" data-id="element-2588">
                Ready to pass your next certification?
              </p>
              <p className="text-sm text-[#5A6B7B]" data-id="element-2589">
                You&apos;ve got the goal. We&apos;ll help you get there.
              </p>
            </div>
          </div>
          <div className="flex w-full flex-col gap-3 sm:flex-row md:w-auto" data-id="element-2590">
            <Link to="/rd-exam" className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5" style={{
            backgroundColor: BLUE
          }} data-id="element-2591">
              Explore RD Exam <ArrowRightIcon className="h-4 w-4" data-id="element-2592" />
            </Link>
            <Link to="/cnsc-exam" className="inline-flex items-center justify-center gap-2 rounded-full border-2 px-6 py-3 text-sm font-semibold transition-colors hover:bg-[#0F9E8E]/5" style={{
            borderColor: TEAL,
            color: TEAL
          }} data-id="element-2593">
              Explore CNSC Exam <ArrowRightIcon className="h-4 w-4" data-id="element-2594" />
            </Link>
          </div>
        </div>

        <p className="mx-auto mt-8 max-w-3xl text-center text-xs leading-relaxed text-[#94A3B8]" data-id="element-2595">
          RD Ready is an independent study resource and is not affiliated with
          or endorsed by any certification board. Use of RD Ready does not
          guarantee examination outcomes.
        </p>
      </section>
    </div>;
}
function ProgramCard({
  accent,
  bg,
  icon: Icon,
  title,
  blurb,
  points,
  cta,
  to
}: {
  accent: string;
  bg: string;
  icon: typeof BookOpenIcon;
  title: string;
  blurb: string;
  points: string[];
  cta: string;
  to: string;
}) {
  return <motion.article initial={{
    opacity: 0,
    y: 24
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-70px'
  }} transition={{
    duration: 0.55
  }} className="rounded-3xl p-7 md:p-9" style={{
    backgroundColor: bg
  }} data-id="element-2596">
      <div className="flex items-start gap-4" data-id="element-2597">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white shadow-sm" data-id="element-2598">
          <Icon className="h-7 w-7" style={{
          color: accent
        }} strokeWidth={1.9} data-id="element-2599" />
        </span>
        <div data-id="element-2600">
          <h2 className="text-2xl font-bold" style={{
          color: accent
        }} data-id="element-2601">
            {title}
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-[#5A6B7B]" data-id="element-2602">{blurb}</p>
        </div>
      </div>
      <ul className="mt-6 space-y-3" data-id="element-2603">
        {points.map(point => <li key={point} className="flex items-center gap-3 text-sm text-[#334155]" data-id="element-2604">
            <CheckCircle2Icon className="h-5 w-5 shrink-0" style={{
          color: accent
        }} data-id="element-2605" />
            {point}
          </li>)}
      </ul>
      <Link to={to} className="mt-7 inline-flex items-center gap-2 text-sm font-semibold transition-transform hover:translate-x-0.5" style={{
      color: accent
    }} data-id="element-2606">
        {cta} <ArrowRightIcon className="h-4 w-4" data-id="element-2607" />
      </Link>
    </motion.article>;
}